// Copyright (c) Docugami, Inc. All rights reserved.

import { BuildStatusContext, NotificationModel, NotificationType, WorkPathCtaContext, WorkPathStatusContext } from "networking/NotificationsApi";
import { TelemetryExceptions } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { BuildStatusNotification } from './BuildStatusNotification';
import { WorkPathCtaNotification } from './WorkPathCtaNotification';
import { WorkPathStatusNotification } from './WorkPathStatusNotification';

export interface INotificationComponentFactory {
    createNotificationComponent(notification: NotificationModel): JSX.Element | null;
}

/**
 * Factory class for creating the appropriate component UX for a given notification object
 */
export class NotificationComponentFactory implements INotificationComponentFactory {
    private readonly _factoryLookup = new Map([
        [NotificationType.BuildStatus, this.createBuildStatusNotification],
        [NotificationType.WorkPathCta, this.createWorkPathCtaNotification],
        [NotificationType.WorkPathStatus, this.createWorkPathStatusNotification],
    ]);

    public createNotificationComponent(notification: NotificationModel): JSX.Element | null {
        const factoryFn = this._factoryLookup.get(notification.notificationType);
        if (!factoryFn) {
            throw new Error(`Failed to find the appropriate factory for the given notification. Type: ${notification.notificationType}`);
        }

        const result = factoryFn(notification);
        if (!result) {
            Telemetry.trackExceptionEvent(
                TelemetryExceptions.NotificationCreationFailed,
                { id: notification.id, type: notification.notificationType, context: notification.context, source: notification.source });
        }

        return result;
    }

    private createBuildStatusNotification(notification: NotificationModel): JSX.Element | null {
        return <BuildStatusNotification
            model={notification}
            context={notification.context.model as BuildStatusContext} />
    }

    private createWorkPathCtaNotification(notification: NotificationModel): JSX.Element {
        return <WorkPathCtaNotification
            model={notification}
            context={notification.context.model as WorkPathCtaContext} />
    }

    private createWorkPathStatusNotification(notification: NotificationModel): JSX.Element {
        return <WorkPathStatusNotification
            model={notification}
            context={notification.context.model as WorkPathStatusContext} />
    }
}
